import { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Calendar, Star, Download, RefreshCw } from 'lucide-react';
import { analyticsService, type ProviderAnalytics } from '@/services/analyticsService';
import { ProgressBar } from '@/components/ui/ProgressIndicator';

interface AnalyticsDashboardProps {
  providerId: string;
}

export function AnalyticsDashboard({ providerId }: AnalyticsDashboardProps) {
  const [analytics, setAnalytics] = useState<ProviderAnalytics | null>(null);
  const [revenueTrends, setRevenueTrends] = useState<Array<{ date: string; revenue: number; jobs: number }>>([]);
  const [servicePerformance, setServicePerformance] = useState<Array<{
    service: string;
    jobs: number;
    revenue: number;
    averageValue: number;
    satisfaction: number;
    growth: number;
  }>>([]);
  const [peakHours, setPeakHours] = useState<Array<{ hour: number; bookings: number; revenue: number; utilization: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState<'WEEKLY' | 'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [isExporting, setIsExporting] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadAnalytics();
  }, [providerId, period]);

  // Handle click outside for export dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      
      // Set date range based on period
      switch (period) {
        case 'WEEKLY':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'MONTHLY':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'YEARLY':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
      }

      const [analyticsData, trendsData, performanceData, peakHoursData] = await Promise.all([
        analyticsService.getProviderAnalytics(providerId, period, startDate, endDate),
        analyticsService.getRevenueTrends(providerId, period === 'WEEKLY' ? 7 : period === 'MONTHLY' ? 30 : 365),
        analyticsService.getServicePerformance(providerId, startDate, endDate),
        analyticsService.getPeakHoursAnalysis(providerId)
      ]);

      setAnalytics(analyticsData);
      setRevenueTrends(trendsData);
      setServicePerformance(performanceData);
      
      // Transform peak hours data to match expected interface
      const transformedPeakHours = peakHoursData.map((item: any) => ({
        hour: item.hour,
        bookings: item.bookingCount || 0,
        revenue: item.averageValue || 0,
        utilization: item.utilization || 0
      }));
      setPeakHours(transformedPeakHours);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (format: 'CSV' | 'PDF' | 'EXCEL') => {
    setIsExporting(true);
    try {
      const exportUrl = await analyticsService.exportAnalytics(providerId, format);
      // In real app, would trigger download
      console.log('Export URL:', exportUrl);
      alert(`Analytics exported as ${format}!`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getTrendIcon = (value: number) => {
    return value >= 0 ? (
      <TrendingUp className="w-4 h-4 text-green-500" />
    ) : (
      <TrendingDown className="w-4 h-4 text-red-500" />
    );
  };

  const getTrendColor = (value: number) => {
    return value >= 0 ? 'text-green-600' : 'text-red-600';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Failed to load analytics data.</p>
        <button onClick={loadAnalytics} className="btn btn-primary mt-4">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600">
            Business insights for {period.toLowerCase()} period
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Period Selector */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as typeof period)}
            className="input input-sm"
          >
            <option value="WEEKLY">Last 7 Days</option>
            <option value="MONTHLY">Last 30 Days</option>
            <option value="YEARLY">Last Year</option>
          </select>

          {/* Export Dropdown */}
          <div className="relative" ref={exportDropdownRef}>
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="btn btn-outline btn-sm flex items-center"
              disabled={isExporting}
            >
              <Download className="w-4 h-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
            {showExportDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-32">
                <div className="py-1">
                  <button 
                    onClick={() => {
                      handleExport('CSV');
                      setShowExportDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    CSV
                  </button>
                  <button 
                    onClick={() => {
                      handleExport('EXCEL');
                      setShowExportDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Excel
                  </button>
                  <button 
                    onClick={() => {
                      handleExport('PDF');
                      setShowExportDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    PDF
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={loadAnalytics}
            className="btn btn-outline btn-sm flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="analytics-metrics">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(analytics.totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="flex items-center mt-2">
            {getTrendIcon(analytics.revenueGrowth)}
            <span className={`text-sm font-medium ml-1 ${getTrendColor(analytics.revenueGrowth)}`}>
              {formatPercentage(analytics.revenueGrowth)}
            </span>
            <span className="text-sm text-gray-500 ml-1">vs last period</span>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalJobs}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="flex items-center mt-2">
            {getTrendIcon(analytics.jobGrowth)}
            <span className={`text-sm font-medium ml-1 ${getTrendColor(analytics.jobGrowth)}`}>
              {formatPercentage(analytics.jobGrowth)}
            </span>
            <span className="text-sm text-gray-500 ml-1">vs last period</span>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Job Value</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(analytics.averageJobValue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-3">
            <ProgressBar 
              progress={(analytics.completionRate * 100)}
              label="Completion Rate"
              showPercentage={true}
              size="md"
              variant="success"
            />
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Customer Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.customerSatisfaction.toFixed(1)}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="flex items-center mt-2">
            <span className="text-sm text-gray-600">
              Response Time: {analytics.averageResponseTime.toFixed(1)}h
            </span>
          </div>
        </div>
      </div>

      {/* Revenue Trends Chart */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trends</h3>
        <div className="h-64 flex items-end space-x-2">
          {revenueTrends.slice(-14).map((trend, index) => {
            const maxRevenue = Math.max(...revenueTrends.map(t => t.revenue));
            const height = (trend.revenue / maxRevenue) * 100;
            
            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-primary-500 rounded-t hover:bg-primary-600 transition-colors cursor-pointer"
                  style={{ height: `${height}%` }}
                  title={`${new Date(trend.date).toLocaleDateString()}: ${formatCurrency(trend.revenue)}`}
                />
                <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-left">
                  {new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Service Performance */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Performance</h3>
          <div className="space-y-4">
            {servicePerformance.map((service, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{service.service}</h4>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span>{service.jobs} jobs</span>
                    <span>{formatCurrency(service.revenue)}</span>
                    <span>★ {service.satisfaction}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${getTrendColor(service.growth)}`}>
                    {formatPercentage(service.growth)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatCurrency(service.averageValue)} avg
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Peak Hours */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Peak Hours Analysis</h3>
          <div className="space-y-3">
            {peakHours.map((hour, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-900 w-16">
                    {hour.hour}:00
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full"
                      style={{ width: `${hour.utilization}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {hour.bookings} bookings
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatCurrency(hour.revenue)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Rates</h3>
          <div className="space-y-4">
            <div>
              <ProgressBar 
                progress={(analytics.bookingConversionRate * 100)}
                label="Booking Conversion"
                showPercentage={true}
                size="sm"
                variant="primary"
              />
            </div>
            <div>
              <ProgressBar 
                progress={(analytics.repeatCustomerRate * 100)}
                label="Repeat Customers"
                showPercentage={true}
                size="sm"
                variant="warning"
              />
            </div>
            <div>
              <ProgressBar 
                progress={(analytics.completionRate * 100)}
                label="Job Completion"
                showPercentage={true}
                size="sm"
                variant="success"
              />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Services</h3>
          <div className="space-y-2">
            {analytics.topServices.slice(0, 3).map((service, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-600 truncate">{service.service}</span>
                <span className="font-medium">{service.count} jobs</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth Trends</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Revenue Growth</span>
              <div className="flex items-center">
                {getTrendIcon(analytics.revenueGrowth)}
                <span className={`font-medium ml-1 ${getTrendColor(analytics.revenueGrowth)}`}>
                  {formatPercentage(analytics.revenueGrowth)}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Job Growth</span>
              <div className="flex items-center">
                {getTrendIcon(analytics.jobGrowth)}
                <span className={`font-medium ml-1 ${getTrendColor(analytics.jobGrowth)}`}>
                  {formatPercentage(analytics.jobGrowth)}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Customer Growth</span>
              <div className="flex items-center">
                {getTrendIcon(analytics.customerGrowth)}
                <span className={`font-medium ml-1 ${getTrendColor(analytics.customerGrowth)}`}>
                  {formatPercentage(analytics.customerGrowth)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}